/** Shapes the map/timeline read models into what MapLibre draws. Pure, so it is tested without WebGL. */
import { COLOR_HEX, PALETTE, type PaletteColor } from "@trip/domain/palette";

export type MapLocation = {
  lat: number;
  lon: number;
  source: "METADATA" | "TRACK_MATCH" | string | null;
  confidence: number | null;
  gapMs: number | null;
};
export type MapAsset = {
  assetId: string;
  contributorId: string;
  kind: string;
  captureAt: string;
  location: MapLocation | null;
  locationStatus: string;
  /** Whether a thumbnail has been rendered for it; absent on an older server. */
  thumbReady?: boolean;
};
export type Track = {
  userId: string;
  displayName: string;
  /** The member's colour on this trip; absent on an older server. */
  color?: PaletteColor;
  points: number[][]; // [epochMs, lat, lon]
  truncated: boolean;
};

type Point = { type: "Point"; coordinates: [number, number] };
type Line = { type: "LineString"; coordinates: [number, number][] };
export type PointFeature = {
  type: "Feature";
  geometry: Point;
  properties: {
    assetId: string;
    kind: string;
    contributorId: string;
    estimated: boolean;
    t: number;
  };
};
export type LineFeature = {
  type: "Feature";
  geometry: Line;
  /** `band` indexes [SPEED_BANDS]; absent on a line that is not a travelled route. */
  properties: { userId: string; name: string; color: string; band?: number };
};

/**
 * A route is drawn in its member's own colour.
 *
 * It used to be handed out by the order tracks came back in, which meant a member who had not
 * moved that day dropped out of the list and shifted everybody else's line to a new colour. A
 * colour that moves between people is worse than no colour: it is a label that lies.
 *
 * The light value always, even in dark mode: the map's tiles are the light style whatever the
 * page is wearing, so a line is read against them and not against the page.
 *
 * The fallback is for an older server that does not send one, and keeps the old behaviour of
 * being at least distinct within one response.
 */
export const routeColor = (t: Track, index: number): string =>
  t.color ? COLOR_HEX[t.color] : COLOR_HEX[PALETTE[index % PALETTE.length]!];

export function toPointFeatures(assets: MapAsset[]) {
  return {
    type: "FeatureCollection" as const,
    features: assets
      .filter((a) => a.location !== null)
      .map((a): PointFeature => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [a.location!.lon, a.location!.lat] },
        properties: {
          assetId: a.assetId,
          kind: a.kind,
          contributorId: a.contributorId,
          estimated: a.location!.source !== "METADATA",
          t: Date.parse(a.captureAt),
        },
      })),
  };
}

/**
 * How fast the traveller was, written into the line itself.
 *
 * Speed is the one thing a route cannot say on its own: a straight kilometre looks the same
 * whether it was strolled or driven. Each stretch is shaded by the pace it was covered at, so
 * the shape of the day — the long still hours, the walk, the drive out — is readable without
 * touching anything.
 *
 * Deeper means faster, which is the opposite of the obvious choice and the only one that
 * works: the map's tiles are nearly white, so a lighter line has less contrast against them,
 * and "brighter when faster" would make the fastest stretches - the ones worth spotting - the
 * faintest things on the screen. Pale is standing still; deep is moving.
 *
 * Five bands rather than a continuous gradient, for two reasons. A band means something you can
 * name - this is walking pace, that is a vehicle - where a smooth wash only says "more". And a
 * run of segments sharing a band collapses into one line, which is what keeps a track of a
 * hundred thousand points from becoming a hundred thousand map features.
 *
 * Every lift is negative, including the slowest: the route layer draws at 75 % opacity, so each
 * shade is composited onto near-white tiles before anyone sees it, and the palest band measured
 * 2.55:1 against them before the whole scale was taken down a notch. All five now clear 3:1
 * composited, with the fastest around three times the contrast of the slowest.
 */
export const SPEED_BANDS: { upTo: number; lift: number; label: string }[] = [
  { upTo: 0.5, lift: -0.02, label: "Still" },
  { upTo: 1.5, lift: -0.07, label: "Ambling" },
  { upTo: 3, lift: -0.13, label: "Walking" },
  { upTo: 8, lift: -0.19, label: "Running or riding" },
  { upTo: Infinity, lift: -0.26, label: "In a vehicle" },
];

/** Which band a speed in metres per second falls in. */
export const bandOf = (mps: number): number => {
  const v = Number.isFinite(mps) && mps > 0 ? mps : 0;
  return SPEED_BANDS.findIndex((b) => v < b.upTo);
};

const EARTH_M = 6_371_000;
const rad = (d: number) => (d * Math.PI) / 180;

/** Great-circle metres between two [lat, lon] pairs. */
export function metresBetween(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = rad(bLat - aLat);
  const dLon = rad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The pace of each step between points, in metres per second.
 *
 * A lower bound on the truth, deliberately: the route has already been simplified, so the
 * straight line between two kept points is never longer than the path actually walked, and the
 * timestamps are the original ones. It will call a twisty lane slower than it was; it will
 * never call a stroll a car.
 *
 * Smoothed with a five-step median, because a single GPS fix landing a few metres off reads as
 * a sprint and an unsmoothed line turns into confetti wherever the sky is narrow. Five and not
 * three: one displaced fix spoils the step into it and the step out of it, so a three-wide
 * median can still come back holding one of the pair.
 */
export function speeds(points: number[][]): number[] {
  const raw: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const dt = (b[0]! - a[0]!) / 1000;
    raw.push(dt > 0 ? metresBetween(a[1]!, a[2]!, b[1]!, b[2]!) / dt : 0);
  }
  return raw.map((_, i) => {
    const w = [raw[i - 2], raw[i - 1], raw[i], raw[i + 1], raw[i + 2]].filter(
      (v): v is number => v !== undefined,
    );
    return w.sort((x, y) => x - y)[Math.floor(w.length / 2)]!;
  });
}

const hexToHsl = (hex: string): [number, number, number] => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === r
      ? ((g - b) / d + (g < b ? 6 : 0)) / 6
      : max === g
        ? ((b - r) / d + 2) / 6
        : ((r - g) / d + 4) / 6;
  return [h, s, l];
};

const hslToHex = (h: number, s: number, l: number): string => {
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/** The member's colour, taken deeper or paler for one speed band. Hue never moves. */
export function shadeForBand(hex: string, band: number): string {
  const [h, s, l] = hexToHsl(hex);
  const lift = (SPEED_BANDS[band] ?? SPEED_BANDS[0]!).lift;
  return hslToHex(h, s, Math.max(0, Math.min(1, l + lift)));
}

/** The same, from a speed rather than a band. */
export const shadeForSpeed = (hex: string, mps: number): string => shadeForBand(hex, bandOf(mps));

/**
 * Longer than this between two fixes and the recorder was off, not standing still.
 *
 * Fixes come a minute or two apart while the recorder is running, so a quarter of an hour of
 * silence is a hole in the recording. The path is still drawn straight across it — a route with
 * bites taken out of it is not what anybody wants to look at — but the *traveller* is not walked
 * across it. On this trip one such hole runs from 9pm to 7am and spans 13km of city, close to
 * 6000 pixels at the zoom the map settles to, and gliding the needle along it at a steady pace
 * meant every turn of the dial threw it most of a screen and dragged the camera after it. So the
 * line stays a line, and the needle waits at the last fix until the recorder finds it again.
 */
export const GAP_MS = 15 * 60 * 1000;

/**
 * One line per run of steps covered at the same pace, so a day of walking is a handful of
 * features rather than one per GPS fix.
 */
function pacedFeatures(points: number[][], track: Track, hex: string): LineFeature[] {
  if (points.length < 2) return [];
  const bands = speeds(points).map(bandOf);
  const out: LineFeature[] = [];
  let start = 0;
  for (let i = 1; i <= bands.length; i++) {
    if (i < bands.length && bands[i] === bands[start]) continue;
    // A run of steps covers points [start, i]: one more point than steps.
    out.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: points.slice(start, i + 1).map((p) => [p[2]!, p[1]!] as [number, number]),
      },
      properties: {
        userId: track.userId,
        name: track.displayName,
        color: shadeForBand(hex, bands[start]!),
        band: bands[start]!,
      },
    });
    start = i;
  }
  return out;
}

export function routeLines(tracks: Track[]) {
  return {
    type: "FeatureCollection" as const,
    features: tracks
      .map((t, i) => ({ t, color: routeColor(t, i) }))
      .filter(({ t }) => t.points.length >= 2)
      .flatMap(({ t, color }) => pacedFeatures(t.points, t, color)),
  };
}

/**
 * The stretch of route this phone has recorded but not pushed yet, as one line. Drawn apart from
 * the server routes (dashed, in the live colour) because it means something different: it is the
 * only copy, and it is the part nobody else on the trip can see yet.
 */
export function localTrackLine(pending: { lat: number; lon: number }[]): LineFeature | null {
  if (pending.length < 2) return null;
  return {
    type: "Feature",
    geometry: { type: "LineString", coordinates: pending.map((p) => [p.lon, p.lat]) },
    properties: { userId: "me", name: "Not uploaded yet", color: LIVE_COLOR },
  };
}

/** The not-yet-uploaded trail. Dashed and in your own colour, so it still reads as you. */
export const LIVE_COLOR = "#1d4ed8";

/** Plain words for where a recording's location came from; a guess is never presented as certain. */
export function describeLocation(a: MapAsset): string {
  if (a.locationStatus === "PENDING") return "Location not worked out yet";
  if (!a.location) return "No location";
  if (a.location.source === "METADATA") return "From the file’s GPS";
  const pct = Math.round((a.location.confidence ?? 0) * 100);
  return `Estimated from the route (${pct} % confidence)`;
}

/**
 * The scrubber's time window: `position` 0..1 slides a window of `widthMs` across [from, to];
 * a null width means the whole range.
 */
export function windowAt(from: number, to: number, position: number, widthMs: number | null) {
  if (widthMs === null || widthMs >= to - from) return { start: from, end: to };
  const p = Math.min(1, Math.max(0, position));
  const start = from + (to - from - widthMs) * p;
  return { start, end: start + widthMs };
}

export const inWindow = (a: MapAsset, w: { start: number; end: number }) => {
  const t = Date.parse(a.captureAt);
  return t >= w.start && t <= w.end;
};

/**
 * How many of a track's points were recorded at or before `t`. A track's points carry their own
 * timestamps and arrive in order, so the travelled part of a route is a prefix of the array and
 * finding it is a binary search — no refetch, and nothing to recompute per frame but an index.
 */
export function sliceUntil(points: number[][], t: number): number {
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid]![0]! <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Where the traveller was at `t`, interpolated between the points on either side, as [lon, lat].
 * Never extrapolates: before the first point and after the last one the nearest end stands, which
 * is what a playhead parked outside a person's own recording window should show.
 */
export function positionAt(points: number[][], t: number): [number, number] | null {
  const leg = legAt(points, t);
  if (!leg) return null;
  const { a, b, span } = leg;
  // Inside a hole the traveller is wherever they were last seen, and stays there until the
  // recorder picks them up again. Sliding them across it would be inventing the journey, and
  // a thumb on the dial would send them flying across the city at a steady pace.
  if (span > GAP_MS) return [a[2]!, a[1]!];
  return along(a, b, span, t);
}

/**
 * Where the line passes at `t`, holes and all.
 *
 * The geometry's answer rather than the traveller's: a day cut out of a track needs a point on
 * each of its edges, and a day that falls inside a hole still has the line crossing it. What is
 * unknown governs where the needle may be put, not whether there is a line to cut.
 */
function lineAt(points: number[][], t: number): [number, number] | null {
  const leg = legAt(points, t);
  if (!leg) return null;
  const { a, b, span } = leg;
  return along(a, b, span, t);
}

/** The two fixes `t` falls between, or the one it rests on at either end. */
function legAt(points: number[][], t: number): { a: number[]; b: number[]; span: number } | null {
  if (points.length === 0) return null;
  const i = sliceUntil(points, t);
  if (i === 0) {
    const first = points[0]!;
    return { a: first, b: first, span: 0 };
  }
  if (i >= points.length) {
    const last = points[points.length - 1]!;
    return { a: last, b: last, span: 0 };
  }
  const a = points[i - 1]!;
  const b = points[i]!;
  return { a, b, span: b[0]! - a[0]! };
}

/** The point `t` of the way along a leg, as [lon, lat]. */
function along(a: number[], b: number[], span: number, t: number): [number, number] {
  const f = span <= 0 ? 0 : (t - a[0]!) / span;
  return [a[2]! + (b[2]! - a[2]!) * f, a[1]! + (b[1]! - a[1]!) * f];
}

/**
 * Each track clipped to [from, to].
 *
 * A day's map should show a day's walking. Carrying yesterday's line across it says the
 * traveller was in two places, and tomorrow's line gives away where they are going to be; both
 * make the day being looked at harder to read, which is the only thing the screen is for.
 *
 * Where a track crosses the boundary the end is interpolated, so the line begins exactly at
 * midnight rather than at whichever point happened to be recorded first after it — the same
 * assumption [positionAt] already makes about where someone was between two fixes. A track that
 * merely starts late is left alone: there is nothing to interpolate from.
 */
export function tracksBetween(tracks: Track[], from: number, to: number): Track[] {
  return tracks.map((t) => ({ ...t, points: pointsBetween(t.points, from, to) }));
}

function pointsBetween(points: number[][], from: number, to: number): number[][] {
  if (points.length === 0) return [];
  const firstT = points[0]![0]!;
  const lastT = points[points.length - 1]![0]!;
  if (lastT < from || firstT > to) return [];

  const inner = points.filter((p) => p[0]! >= from && p[0]! <= to);
  const out: number[][] = [];
  // Only where the track really crosses the edge, and never a second point on the same instant.
  if (firstT < from && lastT > from && inner[0]?.[0] !== from) {
    const at = lineAt(points, from);
    if (at) out.push([from, at[1], at[0]]);
  }
  out.push(...inner);
  const last = inner[inner.length - 1];
  if (lastT > to && firstT < to && last?.[0] !== to) {
    const at = lineAt(points, to);
    if (at) out.push([to, at[1], at[0]]);
  }
  return out;
}

/**
 * The travelled part of each route at `t`, coloured exactly as [routeLines] colours the whole
 * trip, so the solid line and the ghost beneath it are always the same person's colour.
 */
export function travelledLines(tracks: Track[], t: number) {
  return {
    type: "FeatureCollection" as const,
    features: tracks
      .map((track, i) => ({ track, color: routeColor(track, i) }))
      .map(({ track, color }) => ({ track, color, kept: sliceUntil(track.points, t) }))
      // One fix is enough to have walked from: the needle's own position finishes the line, so
      // the trail starts the moment the clock passes the day's first heartbeat rather than
      // waiting for its second — which left the first stretch of every day showing only the
      // faint whole route, as though nothing had been walked yet.
      .filter(({ kept }) => kept >= 1)
      .flatMap(({ track, color, kept }) =>
        pacedFeatures(walkedTo(track.points, kept, t), track, color),
      ),
  };
}

/**
 * The points walked by `t`, ending exactly where the traveller was at that instant.
 *
 * Fixes are minutes apart, and the needle is drawn between them — so a line that stops at the
 * last fix trails behind the needle for as long as the gap lasts, which reads as the line being
 * broken rather than as the clock being between two heartbeats. The interpolated position is
 * added as a final point, carrying the timestamp it stands for so the pace of that last leg is
 * measured over the time actually travelled.
 */
function walkedTo(points: number[][], kept: number, t: number): number[][] {
  const walked = points.slice(0, kept);
  const next = points[kept];
  const last = walked[walked.length - 1];
  if (!next || !last || t <= last[0]!) return walked;
  const span = next[0]! - last[0]!;
  // The next fix is on the far side of a hole: the trail ends at the last thing known.
  if (span > GAP_MS) return walked;
  const f = span <= 0 ? 0 : (t - last[0]!) / span;
  walked.push([t, last[1]! + (next[1]! - last[1]!) * f, last[2]! + (next[2]! - last[2]!) * f]);
  return walked;
}

/**
 * How many of `times` fall in each of `buckets` equal slices of [from, to].
 *
 * The density of a span, for drawing behind a scrubber. Counted here rather than asked for,
 * because the page that needs it already holds every recording's time: a histogram of one day is
 * a loop over what is in hand, not a second round trip to PostGIS.
 */
export function bucketCounts(times: number[], from: number, to: number, buckets: number): number[] {
  const span = to - from;
  if (!(span > 0) || buckets < 1) return [];
  const out = new Array<number>(buckets).fill(0);
  for (const t of times) {
    const i = bucketOf(t, from, span, buckets);
    if (i >= 0) out[i] = out[i]! + 1;
  }
  return out;
}

/**
 * The latest of `times` in each of those same slices, or null where a slice holds none.
 *
 * The companion to [bucketCounts], for a histogram whose bars can be picked: the bar says how
 * much was filmed in that slice and this says where in it to put the playhead — the last thing
 * filmed there, which is the answer the day strip already gives for a whole day.
 */
export function bucketLast(
  times: number[],
  from: number,
  to: number,
  buckets: number,
): (number | null)[] {
  const span = to - from;
  if (!(span > 0) || buckets < 1) return [];
  const out = new Array<number | null>(buckets).fill(null);
  for (const t of times) {
    const i = bucketOf(t, from, span, buckets);
    if (i >= 0 && (out[i] === null || t > out[i]!)) out[i] = t;
  }
  return out;
}

/** Which slice an instant falls in, or -1 for one outside the span altogether. */
function bucketOf(t: number, from: number, span: number, buckets: number): number {
  if (!Number.isFinite(t) || t < from || t > from + span) return -1;
  // The closing instant divides out to `buckets` exactly; it belongs to the last bar rather
  // than to one past the end.
  return Math.min(buckets - 1, Math.floor(((t - from) / span) * buckets));
}
